import { useState, useEffect, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { setCacheUserId, startRealtimeSync, stopRealtimeSync } from '@/hooks/useSupabaseStore';

export type UserRole = 'admin' | 'staff' | 'super_admin';

export interface AuthState {
  user: User | null;
  session: Session | null;
  role: UserRole | null;
  displayName: string;
  loading: boolean;
  isAdmin: boolean;
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(true);
  const initialized = useRef(false);

  const fetchRole = useCallback(async (userId: string) => {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('role, display_name')
        .eq('id', userId)
        .maybeSingle();
      if (data) {
        setRole(data.role as UserRole);
        setDisplayName((data.display_name as string) || '');
      } else {
        setRole(null);
        setDisplayName('');
      }
    } catch {
      setRole(null);
      setDisplayName('');
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      setLoading(true);
      const { data, error } = await supabase.auth.getSession();
      if (!mounted) return;

      if (error) {
         
        console.error('Auth init error:', error);
      }

      const currentSession = data.session;
      setSession(currentSession);
      const currentUser = currentSession?.user ?? null;
      setUser(currentUser);
      setCacheUserId(currentUser?.id ?? null);

      if (currentUser) {
        startRealtimeSync();
        await fetchRole(currentUser.id);
      } else {
        setRole(null);
      }

      if (mounted) {
        setLoading(false);
        initialized.current = true;
      }
    };

    init();

    const { data: listener } = supabase.auth.onAuthStateChange((event, s) => {
      if (!mounted) return;
      // Bỏ qua event trong lúc init chưa xong để tránh race condition
      if (!initialized.current) return;

      setSession(s);
      const currentUser = s?.user ?? null;
      setUser(currentUser);
      setCacheUserId(currentUser?.id ?? null);
      if (!currentUser) {
        setRole(null);
        stopRealtimeSync();
      } else {
        startRealtimeSync();
        fetchRole(currentUser.id);
      }
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, [fetchRole]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
       
      console.error('Login error:', error.message, '| code:', error.code, '| status:', error.status);
    }
    return { error };
  };

  const signUp = async (email: string, password: string, displayName?: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { display_name: displayName || email.split('@')[0] },
        emailRedirectTo: window.location.origin,
      },
    });
    return { data, error };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}${__BASE_PATH__}` },
    });
    return { error };
  };

  // Đăng nhập không cần mật khẩu: gửi mã OTP qua email
  const signInWithOtp = async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
        emailRedirectTo: window.location.origin,
      },
    });
    return { error };
  };

  const verifyOtp = async (email: string, token: string) => {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    });
    return { data, error };
  };

  const updateDisplayName = async (name: string) => {
    if (!user) return { error: new Error('Chưa đăng nhập') };
    const trimmed = name.trim();
    if (!trimmed) return { error: new Error('Tên không được để trống') };
    const { error } = await supabase
      .from('profiles')
      .update({ display_name: trimmed })
      .eq('id', user.id);
    if (!error) setDisplayName(trimmed);
    return { error };
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (err) {
       
      console.error('Sign out error:', err);
      throw err;
    }
  };

  return {
    user,
    session,
    role,
    displayName,
    loading,
    isAdmin: role === 'admin',
    isSuperAdmin: role === 'super_admin',
    signIn,
    signUp,
    signInWithGoogle,
    signInWithOtp,
    verifyOtp,
    updateDisplayName,
    signOut,
  };
}